import React from 'react';
import { useMachine } from '@xstate/react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { StaticGmap } from '@kyruus/gmap';
import { AwsMap } from '@kyruus/aws-map';
import { WrappedComponentProps } from '@kyruus/intl';
import { PMCConfig } from '@kyruus/types';
import { ShimmeringLoaderBar } from '@kyruus/loaders-and-spinners';

import { getConfigProperty } from 'Common/config';
import { AppSettings, MarketableLocation } from 'Src/types';
import { ContentSection, SectionHeader } from '../styles';
import messages from '../messages';
import { Container, MapLoadingContainer } from './styles';
import { createMapMachine, STATES } from './machine';
import { getAwsMapLocation, locationToMapAddress } from './utils';
import { shouldRenderLocationSection } from '../utils';
import {
  SMALL_IMAGE_SIZE,
  MEDIUM_IMAGE_SIZE,
  LARGE_IMAGE_SIZE,
  SMALL_MAP_HEIGHT,
  MEDIUM_MAP_HEIGHT,
  LARGE_MAP_HEIGHT,
  MAP_PROVIDERS
} from './constants';

interface LocationProps extends WrappedComponentProps {
  appSettings: AppSettings;
  config: PMCConfig;
  marketableLocation: MarketableLocation;
}

export const Location = injectIntl(
  ({ marketableLocation, appSettings, config, intl }: LocationProps) => {
    const { id: locationId } = marketableLocation || {};
    const mapProvider = getConfigProperty(
      config,
      'maps_config.location_service_provider'
    );

    const [state] = useMachine(
      createMapMachine({
        provider: mapProvider,
        locations: [locationId],
        zoom: 14
      })
    );

    if (!shouldRenderLocationSection(marketableLocation)) {
      return null;
    }

    if (mapProvider === MAP_PROVIDERS.AWS) {
      return (
        <ContentSection>
          <Container data-testid="LocationAwsMap">
            <AwsMap
              awsMapConfig={{
                identityPoolId:
                  appSettings.AWS_MAPS_CREDENTIALS.mapIdentityPoolId,
                mapName: appSettings.AWS_MAPS_CREDENTIALS.mapName,
                region: appSettings.AWS_MAPS_CREDENTIALS.mapRegion
              }}
              locations={[getAwsMapLocation(marketableLocation)]}
              mapOptions={{
                zoom: 13,
                maxZoom: 13
              }}
              isStaticMap={true}
            />
          </Container>
        </ContentSection>
      );
    }

    const { mapUrls } = state.context;

    if (state.matches(STATES.error)) {
      return null;
    }

    const loading =
      state.matches(STATES.loading) ||
      state.matches(STATES.initializing) ||
      !mapUrls;

    return (
      <ContentSection>
        <FormattedMessage {...messages.locationTitle}>
          {(text) => <SectionHeader id="map-title">{text}</SectionHeader>}
        </FormattedMessage>
        {loading && (
          <MapLoadingContainer data-testid="LocationMapLoading">
            <ShimmeringLoaderBar />
          </MapLoadingContainer>
        )}
        {!loading && (
          <Container data-testid="LocationStaticGmap">
            <StaticGmap
              smallImage={mapUrls[SMALL_IMAGE_SIZE]}
              smallHeight={SMALL_MAP_HEIGHT}
              mediumImage={mapUrls[MEDIUM_IMAGE_SIZE]}
              mediumHeight={MEDIUM_MAP_HEIGHT}
              largeImage={mapUrls[LARGE_IMAGE_SIZE]}
              largeHeight={LARGE_MAP_HEIGHT}
              mapLink={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                locationToMapAddress(marketableLocation)
              )}`}
              label={intl.formatMessage(messages.staticMapLabel)}
            />
          </Container>
        )}
      </ContentSection>
    );
  }
);
